import { colors } from '../utils/colors';
import { layoutTokens } from './layoutTokens';
import { ThemePalette } from './types';
import { getEasterPaletteOverrides } from './easterTheme';

const easterPaletteOverrides = getEasterPaletteOverrides(false);

export const corePalette: ThemePalette = {
    // Semantic tokens (no isDarkTheme conditionals in components)
    listItemHoverBackground: colors.gray.broken_white,
    modalListBackground: colors.white.basic,
    modalContentText: 'rgba(0, 0, 0, 0.87)',
    importErrorRowBackground: '#fff1f0',
    stickyHeaderIconColor: 'rgba(0, 0, 0, 0.54)',
    // DataGrid (light theme – optional overrides)
    dataGridHeaderTitleColor: 'rgba(0,0,0,0.87)',
    dataGridPaginationText: 'rgba(0,0,0,0.87)',
    dataGridFooterBackground: colors.white.basic,
    dataGridRowBorderColor: 'rgba(0,0,0,0.12)',
    dataGridCellBorderColor: 'rgba(0,0,0,0.12)',
    dataGridColumnSeparatorColor: 'rgba(0,0,0,0.12)',
    dataGridScrollbarTrack: colors.gray.icewind_dale,
    dataGridScrollbarThumb: colors.gray.cold_gray,
    dataGridScrollbarThumbBorder: colors.gray.icewind_dale,
    dropdownMenuPaperBg: colors.white.basic,
    dropdownMenuItemSelectedBg: 'rgba(0, 0, 0, 0.08)',
    dropdownMenuItemSelectedHoverBg: 'rgba(0, 0, 0, 0.12)',
    contentFilterInputUnderlineBefore: 'rgba(0,0,0,0.23)',
    contentFilterInputUnderlineHover: 'rgba(0,0,0,0.87)',
    contentFilterInputUnderlineAfter: colors.blue.i2x_flashy,
    contentFilterInputBorderRadius: layoutTokens.filterInputBorderRadius,
    contentFilterInputPadding: layoutTokens.filterInputPadding,
    activeSpeakerBarBackground: colors.white.white_smoke,
    cardWatermarkColor: 'rgba(69, 80, 230, 0.11)',
    userStatusActiveBg: 'rgba(39, 157, 121, 0.14)',
    userStatusActiveText: '#1F7A5E',
    userStatusInactiveBg: 'rgba(230, 167, 0, 0.14)',
    userStatusInactiveText: '#8A5F00',
    evaluationViewBasicContainerBg: 'transparent',
    evaluationViewBasicContainerBorder: 'transparent',
    evaluationViewDialogDividerBorder: 'rgba(0,0,0,0.12)',
    evaluationViewRatingContainerBg: '#eceff3',
    filterBorderColor: 'rgba(0,0,0,0.2)',

    surfaceOverlay: 'rgba(231,235,238,0.87)',
    surfaceOverlayHover: 'rgba(0, 0, 0, 0.04)',
    surfaceBorderSubtle: 'rgba(0,0,0,0.38)',
    disabledControlBorder: 'rgba(0, 0, 0, 0.24)',
    disabledControlBg: 'rgba(0, 0, 0, 0.035)',
    // Accent actions
    buttonAccentText: colors.white.basic,
    buttonAccentOutlinedText: colors.blue.i2x_flashy,
    buttonAccentBg: colors.blue.i2x_flashy,
    buttonAccentHoverBg: colors.blue.after_work_blue,
    buttonAccentSoftBg: 'rgba(69, 80, 230, 0.1)',
    buttonAccentSoftHoverBg: 'rgba(69, 80, 230, 0.18)',
    buttonAccentSoftActiveBg: 'rgba(69, 80, 230, 0.08)',
    buttonAccentSoftDisabledBg: 'rgba(69, 80, 230, 0.06)',
    buttonAccentDisabledText: 'rgba(0, 0, 0, 0.38)',
    buttonAccentDisabledBg: 'rgba(0,0,0,0.12)',
    buttonAccentDisabledBorder: 'rgba(0,0,0,0.12)',
    headerFlagGermanStripeBlack: colors.black.default,
    headerFlagGermanStripeRed: colors.red.monza,
    headerFlagGermanStripeGold: '#FFCE00',
    headerFlagEuBackground: '#003399',
    headerFlagEuStars: '#FFCE00',
    headerBadgeText: colors.white.basic,

    // Common
    pageBackground: colors.white.white_smoke,
    modalBackdropBg: 'rgba(2, 6, 23, 0.18)',
    layoutBackground: 'linear-gradient(160deg, #FBFCFF 0%, #F3F7FD 52%, #EAF1FB 100%)',
    layoutPatternGradient: 'radial-gradient(circle, rgba(95, 122, 170, 0.08) 1.2px, transparent 1.2px)',
    layoutIconsPatternFillColor: 'rgba(32,66,130,0.02)',
    contentLinkColor: colors.blue.i2x_flashy,
    contentPaperBackground: 'rgba(255, 255, 255, 0.85)',
    contentPaperBorder: 'rgba(255, 255, 255, 0.74)',
    contentPaperText: 'rgba(0,0,0,0.87)',
    pageNavIconColor: colors.gray.cod_gray,
    contentTableBackground: 'rgba(246, 250, 255, 0.64)',
    contentTableBorder: 'rgba(255, 255, 255, 0.7)',
    contentActionIconColor: colors.blue.i2x_flashy,
    accordionBackground: 'rgba(255, 255, 255, 0.85)',
    accordionBorderColor: 'rgba(15, 23, 42, 0.14)',
    accordionTextColor: 'inherit',
    accordionExpandIconColor: 'inherit',
    tableActionButtonColor: colors.blue.i2x_flashy,
    tableActionButtonBg: colors.blue.i2x_flashy,
    contentSectionIconColor: 'rgba(0, 0, 0, 0.38)',
    contentListBackground: 'rgba(247, 251, 255, 0.66)',
    qualityManagementScoreChipBg: '#E0E0E0',
    qualityManagementScoreChipText: 'rgba(0, 0, 0, 0.87)',
    qualityManagementTopActionOutlinedColor: colors.blue.i2x_flashy,
    qualityManagementTopActionOutlinedBorder: colors.blue.i2x_flashy,
    qualityManagementTopActionOutlinedBg: 'transparent',
    qualityManagementTopActionOutlinedHoverBg: 'rgba(69, 80, 230, 0.04)',
    qualityManagementTopActionContainedBg: colors.blue.i2x_flashy,
    qualityManagementTopActionContainedText: colors.white.basic,
    qualityManagementTopActionOutlinedDisabledText: 'rgba(31, 35, 40, 0.55)',
    qualityManagementTopActionDisabledBorder: 'rgba(69, 80, 230, 0.45)',
    qualityManagementTopActionSoftDisabledBg: 'rgba(69, 80, 230, 0.06)',
    qualityManagementTopActionContainedDisabledText: 'rgba(255, 255, 255, 0.82)',
    qualityManagementTopActionContainedDisabledBg: 'rgba(69, 80, 230, 0.45)',
    qualityManagementTopActionContainedHoverBg: colors.blue.after_work_blue,
    contentChartGridStroke: 'rgba(0, 0, 0, 0.16)',
    contentChartAxisStroke: 'rgba(0, 0, 0, 0.22)',
    contentChartTickFill: 'rgba(0, 0, 0, 0.72)',
    contentChartLineStroke: colors.blue.i2x_flashy,
    contentChartTooltipBg: 'rgba(255, 255, 255, 0.94)',
    contentChartTooltipBorder: 'rgba(255,255,255,0.56)',
    contentChartTooltipText: 'rgba(0, 0, 0, 0.87)',
    headerTabTextColor: colors.gray.dark,
    selectedTabTextColor: colors.blue.i2x_flashy,
    headerTabIndicatorColor: colors.blue.i2x_flashy,
    headerBackground: 'rgba(255, 255, 255, 0.3)',
    headerLogoColor: colors.blue.i2x_flashy,
    headerControlHoverBackground: easterPaletteOverrides.headerControlHoverBackground || 'rgba(0, 0, 0, 0.04)',
    titleText: 'rgba(0,0,0,0.87)',
    borderShadowColor: 'rgba(0,0,0,0.2)',
    mainActionButtonBg: colors.blue.i2x_flashy,
    mainActionButtonHighAlertBg: '#b00020',
    mainActionButtonTextColor: colors.white.basic,
    secondaryActionButtonTextColor: colors.blue.i2x_flashy,
    secondaryDeleteButtonTextColor: colors.red.monza,
    borderColor: 'rgba(0,0,0,0.38)',
    betaTagColor: 'rgba(0,0,0,0.6)',
    inputLabelColor: 'rgba(0,0,0,0.6)',
    secondaryTextColor: 'rgba(0, 0, 0, 0.54)',
    qualityIndexYellow: colors.yellow.neon_carrot,

    // Filters
    buttonColor: colors.blue.i2x_flashy,
    buttonColorOnHover: colors.blue.after_work_blue,
    isDarkTheme: false,
    filterPopupBorder: 'rgba(0, 0, 0, 0.16)',
    whatsNewButtonActiveBg: colors.blue.i2x_flashy,
    whatsNewButtonActiveText: colors.white.basic,
    whatsNewButtonInactiveBg: 'transparent',
    whatsNewButtonInactiveText: colors.blue.i2x_flashy,
    whatsNewButtonInactiveBorder: colors.blue.i2x_flashy,
    modalFooterDisabledText: colors.gray.cold_gray,
    modalTitleText: 'rgba(0,0,0,0.87)',
    modalSubtitleBackground: 'rgba(255, 255, 255, 0.62)',
    autocompleteModalChip: 'rgba(255, 255, 255, 0.72)',
    modalSubtext: colors.gray.charcoal,
    modalTitle: colors.gray.dark,
    modalCheckbox: colors.gray.dove_gray,
    modalFooterPrimaryButtonBg: colors.blue.i2x_flashy,
    modalFooterPrimaryButtonText: colors.white.basic,
    modalFooterPrimaryButtonHoverBg: colors.blue.after_work_blue,
    modalFooterPrimaryButtonDisabledBg: 'rgba(0,0,0,0.12)',
    modalFooterPrimaryButtonDisabledText: 'rgba(0,0,0,0.38)',
    modalFooterSecondaryButtonDisabledText: colors.gray.cold_gray,
    dateRangeLabelHighlighted: colors.blue.i2x_flashy,
    dateRangeText: 'rgba(0, 0, 0, 0.87)',
    dateRangeSelectedDateText: colors.white.basic,
    contentDatePresetButtonHoverBg: 'rgba(255, 255, 255, 0.62)',
    contentDatePresetDisabledText: 'rgba(0, 0, 0, 0.5)',
    contentCalendarAccentBg: '#4a90e2',
    contentCalendarActionColor: colors.blue.i2x_flashy,
    rangePickerMain: colors.blue.i2x_flashy,
    rangePickerBorderBottom: 'rgba(0, 0, 0, 0.42)',
    rangePickerFooterBorder: 'rgb(238, 238, 238)',
    timePickerColor: 'rgba(0, 0, 0, 0.6)',
    removeButtonColor: 'rgba(0, 0, 0, 0.6)',
    downArrowColor: 'rgba(0, 0, 0, 0.54)',

    // Dashboard
    speechRatioProgressBarRight: colors.gray.icewind_dale,
    callsCountSubtext: colors.gray.dove_gray,
    tableProgressCellDefault: colors.blue.i2x_flashy,
    tableLabelText: colors.gray.dark,
    cardFooterBorder: 'rgba(151, 151, 151, 0.5)',
    cardInfoHeaderPopupContent: colors.gray.dove_gray,
    cardTooltipBackground: 'rgba(255, 255, 255, 0.94)',
    dashboardCardBackground: 'rgba(255, 255, 255, 0.85)',
    dashboardHeaderBackground: 'transparent',
    dashboardHeaderTabTextColor: colors.gray.dark,
    dashboardHeaderTabSelectedTextColor: colors.blue.i2x_flashy,
    dashboardHeaderTabIndicatorColor: colors.blue.i2x_flashy,
    dashboardHeaderDateRangeTextColor: 'rgba(0, 0, 0, 0.87)',
    dashboardHeaderDateRangeBorderColor: 'rgba(0, 0, 0, 0.42)',
    dashboardHeaderDateRangeHighlightColor: colors.blue.i2x_flashy,
    dashboardHeaderDateRangeSelectedTextColor: colors.white.basic,
    dashboardCardAccentColor: colors.blue.i2x_flashy,
    dashboardCardSortIconColor: colors.gray.cold_gray,
    cardActionButtonText: colors.blue.i2x_flashy,
    cardActionButtonIcon: 'inherit',
    dashboardSentimentPositive: colors.green.schamrock,
    dashboardSentimentNeutral: colors.gray.star_dust,
    dashboardSentimentNegative: colors.red.fiery_rose,

    // Reports
    reportsCellBorder: colors.gray.icewind_dale,
    reportsCellBackground: 'rgba(255, 255, 255, 0.72)',
    reportsKeywordUseCellBorder: colors.white.cascading_white,
    reportsKeywordSetGroupBackground: colors.blue.light_gray_blue,
    reportsHeadCellText: colors.black.dark_side_of_the_moon,
    reportsText: colors.black.default,
    reportsSubText: colors.gray.dove_gray,

    // Reports v2

    // Call History
    callDetailsSubHeading: colors.gray.dove_gray,
    callHistoryDoSay: colors.green.dark_shamrock,
    callHistoryDoSayComplement: colors.yellow.neon_carrot,
    callHistoryPlayerFilled: colors.gray.kettleman,
    callHistoryProgressBarBackground: colors.white.same,
    callHistorySpeedBorder: colors.gray.noqrei_silver,
    callHistorySpeedSelected: colors.blue.i2x_flashy,
    callHistoryAgentSpeaker: colors.blue.fresh_air,
    callHistorySentimentPositive: colors.green.schamrock,
    callHistorySentimentNegative: colors.red.fiery_rose,
    callHistorySentimentNeutral: colors.gray.star_dust,
    callHistoryCustomerSpeaker: colors.blue.lavender_blue_shadow,
    callHistorySpeakerBarBackground: colors.white.white_smoke,
    callHistorySearchHighlight: colors.yellow.neon_carrot,
    callHistorySearchFaded: colors.yellow.glossy_gold,
    callHistoryTranscriptSearchBackground: 'rgba(255, 255, 255, 0.85)',
    callHistoryTranscriptSearchFocus: 'rgba(0,0,0,0.6)',
    callHistorySearchBoxTitleBorder: colors.white.same,
    callHistorySearchDoSayProgressBarBackground: colors.gray.icewind_dale,
    callHistoryText: colors.black.default,
    callHistoryDontSay: colors.red.light_carmine_pink,
    callHistoryBoxShadow: 'rgba(0, 0, 0, 0.15)',
    callHistoryDisabledWordColor: 'rgba(0, 0, 0, 0.34)',
    callHistoryCurrentWord: colors.blue.loblolly,
    callHistoryAppliedFilterBackground: 'rgba(69,80,230, 0.1)',
    callHistoryTableScoreCardLabelText: colors.white.basic,
    callHistoryTableCallTopicsText: colors.blue.i2x_flashy,
    callHistoryTableCallTopicsBorder: colors.blue.i2x_flashy,
    callHistoryTableCallTopicsBackground: 'rgb(234, 235, 253)',
    callHistoryTableCallTopicsActiveBackground: 'rgb(248, 249, 255)',
    callHistoryTableCallTopicsSeparator: 'rgb(69, 80, 230, 0.3)',
    callHistoryTableNoTopicBackground: 'rgb(230, 230, 230)',
    callHistoryUtteranceHighlightActive: '#f8d193',
    callHistoryUtteranceHighlightGroup: '#f5f9ae',
    callHistoryFlexibleGroupBackground: 'rgba(60, 214, 155, 0.16)',
    callHistoryFlexibleGroupActiveBackground: 'rgba(60, 214, 155, 0.3)',
    callHistoryFlexibleDontSayBackground: 'rgba(255, 80, 110, 0.16)',
    callHistoryFlexibleDontSayActiveBackground: 'rgba(255, 80, 110, 0.3)',
    callHistoryUtteranceAnimationFrom: '#eedabc',
    callHistoryUtteranceHighlightDefault: colors.white.basic,
    callHistoryWordText: colors.black.default,
    callHistoryWordCurrent: 'rgba(0,0,0,0.87)',
    callHistoryTooltipText: 'rgba(0,0,0,0.87)',
    callDetailsLoaderForeground: '#d8d8d8',
    callDetailsLoaderBackground: '#eee',

    // Key Word Manager
    keyWordMangerRowBackground: colors.white.basic,
    keyWordMangerRowDragIcon: colors.gray.aluminum,
    keyWordMangerRowBackgroundEditMode: colors.white.white_smoke,
    keyWordMangerRowBackgroundBorder: colors.white.same,
    keyWordMangerConfirmButtonWrapperEnabled: colors.blue.i2x_flashy,
    keyWordMangerConfirmButtonActiveColor: colors.white.basic,
    keyWordMangerTagInput: colors.gray.aluminum,
    keyWordMangerTagBorder: colors.blue.i2x_flashy,
    keyWordMangerInvalidTagTooltipBg: colors.red.light_carmine_pink,
    keyWordMangerPopupMsgBg: colors.white.basic,
    keyWordMangerPopupMsgColor: colors.black.default,
    keyWordMangerPopupShadow: colors.blue.cadet_blue,
    keyWordMangerStarColor: colors.yellow.star,
    keyWordMangerPhoneIconColor: colors.blue.i2x_flashy,
    keyWordMangerHeaderBorderBottom: colors.gray.mercury,
    keyWordMangerListHeaderBg: '#f8fbff',
    keyWordMangerListHeaderText: colors.gray.dove_gray,
    keyWordMangerDontSay: colors.red.monza,
    keyWordMangerDoSay: colors.green.jade_2,
    keyWordMangerWarning: colors.yellow.neon_carrot
};
